import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CANONICAL_FIELDS,
  PIPELINE_STAGES,
  confirmJobMapping,
  createOrganization,
  getJobExportUrl,
  getJobMapping,
  getJobRecommendations,
  getJobResults,
  getJobStatus,
  uploadDataset,
} from "../api/etlApi";

const STAGE_LABELS = {
  parsing: "Preparing your file",
  mapping: "Checking column matches",
  cleansing: "Cleaning your data",
  loading: "Saving customer records",
  scoring: "Scoring customer value",
  recommendations: "Preparing suggestions",
  done: "Complete",
};

const SEGMENT_CLASS = {
  Champions: "badge-champions",
  "Loyal Customers": "badge-loyal",
  Promising: "badge-potential",
  "Need Attention": "badge-potential",
  "At Risk": "badge-atrisk",
  Lost: "badge-lost",
};

const CLEANING_REASON_LABELS = {
  bad_customer_name: "Bad customer name",
  invalid_amount: "Invalid amount",
  zero_or_negative_amount: "Zero or negative amount",
  customer_total_spend_zero: "Customer total spend zero",
  null_required_fields: "Missing required fields",
  unparseable_date: "Invalid date",
  duplicates: "Duplicate rows",
  invalid_phone_emptied: "Invalid phone emptied",
};

export default function EtlPipelinePanel({
  onNotify,
  onComplete,
  selectedOrgId = "",
  selectedOrgName = "",
  stagedNewOrgName = "",
  onOrganizationCreated,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("upload");
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState("");
  const [status, setStatus] = useState("parsing");
  const [progress, setProgress] = useState({});
  const [errorLog, setErrorLog] = useState("");
  const [mapping, setMapping] = useState({ domain_hint: "", mappings: [] });
  const [results, setResults] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [search, setSearch] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeOrgIdRef = useRef("");

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetWorkflowState = useCallback(() => {
    stopPolling();
    setView("upload");
    setFile(null);
    setJobId("");
    setStatus("parsing");
    setProgress({});
    setErrorLog("");
    setMapping({ domain_hint: "", mappings: [] });
    setResults(null);
    setRecommendations([]);
    setSearch("");
    setIsBusy(false);
    setIsDraggingFile(false);
  }, [stopPolling]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    resetWorkflowState();
  }, [resetWorkflowState]);

  const openPanel = useCallback(() => {
    resetWorkflowState();
    setIsOpen(true);
  }, [resetWorkflowState]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        closePanel();
      }
    }

    if (isOpen) {
      document.body.classList.add("etl-modal-open");
      window.addEventListener("keydown", onKeyDown);
    }

    return () => {
      document.body.classList.remove("etl-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closePanel, isOpen]);

  const filteredRecords = useMemo(() => {
    const records = results?.records || [];
    const query = search.trim().toLowerCase();
    if (!query) return records;
    return records.filter((item) => {
      const text = [item.full_name, item.mobile, item.email].filter(Boolean).join(" ").toLowerCase();
      return text.includes(query);
    });
  }, [results, search]);

  const primaryMetric = useMemo(() => {
    if (view === "processing") {
      return `${progress.customers_upserted ?? 0} customers updated`;
    }
    if (view === "results") {
      return `${results?.records?.length || 0} customers scored`;
    }
    return "Ready to import";
  }, [progress.customers_upserted, results?.records?.length, view]);

  const pipelineFailureReason = useMemo(() => {
    const reasonLabel = String(progress?.failure_reason_label || "").trim();
    const reasonMessage = String(progress?.failure_message || "").trim();
    if (reasonLabel && reasonMessage) {
      return `${reasonLabel}: ${reasonMessage}`;
    }
    return reasonLabel || reasonMessage || "";
  }, [progress?.failure_message, progress?.failure_reason_label]);

  const cleaningReport = progress?.cleaning_report || {};
  const cleaningReasonEntries = useMemo(() => {
    const removed = cleaningReport?.removed_reasons || {};
    const phoneCleanup = cleaningReport?.phone_cleanup_reasons || {};
    return Object.entries({ ...removed, ...phoneCleanup }).filter(([, count]) => Number(count) > 0);
  }, [cleaningReport?.phone_cleanup_reasons, cleaningReport?.removed_reasons]);

  const viewLabel = useMemo(() => {
    if (view === "mapping") return "Review columns";
    if (view === "processing") return STAGE_LABELS[status] || "Processing data";
    if (view === "results") return "Results Ready";
    return "Start import";
  }, [status, view]);

  const lockedBusinessLabel = useMemo(() => {
    if (stagedNewOrgName.trim()) {
      return `${stagedNewOrgName} (new org - will be created on upload)`;
    }
    if (selectedOrgName) {
      return selectedOrgName;
    }
    return "No organization selected";
  }, [selectedOrgName, stagedNewOrgName]);

  function isSupportedUploadFile(fileCandidate) {
    if (!fileCandidate?.name) {
      return false;
    }
    const normalized = fileCandidate.name.toLowerCase();
    return normalized.endsWith(".csv") || normalized.endsWith(".xlsx") || normalized.endsWith(".xls");
  }

  function assignUploadFile(fileCandidate) {
    if (!fileCandidate) {
      return;
    }
    if (!isSupportedUploadFile(fileCandidate)) {
      onNotify?.("Unsupported file type. Please use .csv, .xlsx, or .xls", "error");
      return;
    }
    setFile(fileCandidate);
    onNotify?.(`File selected: ${fileCandidate.name}`, "success");
  }

  function onDropZoneDragOver(event) {
    event.preventDefault();
    setIsDraggingFile(true);
  }

  function onDropZoneDragEnter(event) {
    event.preventDefault();
    setIsDraggingFile(true);
  }

  function onDropZoneDragLeave(event) {
    event.preventDefault();
    const related = event.relatedTarget;
    if (!event.currentTarget.contains(related)) {
      setIsDraggingFile(false);
    }
  }

  function onDropZoneDrop(event) {
    event.preventDefault();
    setIsDraggingFile(false);
    const droppedFile = event.dataTransfer?.files?.[0] || null;
    assignUploadFile(droppedFile);
  }

  async function onUpload() {
    if (!file) {
      onNotify?.("Please select a file first.", "error");
      return;
    }

    try {
      setIsBusy(true);
      let targetOrgId = selectedOrgId;

      if (stagedNewOrgName.trim()) {
        const created = await createOrganization({
          name: stagedNewOrgName.trim(),
        });
        targetOrgId = created.org_id;
        onOrganizationCreated?.(created);
        onNotify?.("New organization created. Starting import.", "success");
      }

      if (!targetOrgId) {
        onNotify?.("Please select a business before starting import.", "error");
        setIsBusy(false);
        return;
      }

      const payload = await uploadDataset({ orgId: targetOrgId, file });
      setJobId(payload.job_id);
      setStatus(payload.status || "parsing");
      setProgress({});
      setErrorLog("");
      setIsOpen(true);
      setView("processing");
      activeOrgIdRef.current = targetOrgId;
      startPolling(payload.job_id, "mapping", targetOrgId);
    } catch (error) {
      onNotify?.(`Import failed: ${error.message}`, "error");
    } finally {
      setIsBusy(false);
    }
  }

  function startPolling(activeJobId, mode, orgIdForJob = "") {
    stopPolling();

    const run = async () => {
      try {
        const current = await getJobStatus(activeJobId);
        setStatus(current.status || "parsing");
        setProgress(current.progress || {});
        setErrorLog(current.error_log || "");

        if (current.status === "failed") {
          stopPolling();
          onNotify?.("Import could not be completed. Please check the message shown.", "error");
          return;
        }

        if (mode === "mapping" && current.status === "awaiting_confirmation") {
          stopPolling();
          const mapData = await getJobMapping(activeJobId);
          setMapping({
            domain_hint: mapData.domain_hint || "",
            mappings: mapData.mappings || [],
            ai_runtime: mapData.ai_runtime || null,
          });
          setIsOpen(true);
          setView("mapping");
          return;
        }

        if (mode === "completion" && current.status === "done") {
          stopPolling();
          const [resultData, recData] = await Promise.all([
            getJobResults(activeJobId),
            getJobRecommendations(activeJobId),
          ]);
          setResults(resultData);
          setRecommendations(recData?.recommendations || []);
          setIsOpen(true);
          setView("results");
          onNotify?.("Import completed successfully.", "success");
          onComplete?.(orgIdForJob || activeOrgIdRef.current || "");
          return;
        }
      } catch (error) {
        onNotify?.(`Could not check import status: ${error.message}`, "error");
        stopPolling();
        return;
      }

      pollRef.current = setTimeout(run, mode === "mapping" ? 1200 : 1600);
    };

    run();
  }

  async function onConfirmMapping() {
    try {
      setIsBusy(true);
      await confirmJobMapping(jobId, mapping.mappings);
      setView("processing");
      startPolling(jobId, "completion", activeOrgIdRef.current);
    } catch (error) {
      onNotify?.(`Could not confirm columns: ${error.message}`, "error");
    } finally {
      setIsBusy(false);
    }
  }

  function updateMapping(index, value) {
    setMapping((prev) => {
      const next = [...prev.mappings];
      next[index] = { ...next[index], canonical_field: value || null };
      return { ...prev, mappings: next };
    });
  }

  function renderCleaningReport() {
    if (!cleaningReasonEntries.length) {
      return null;
    }

    return (
      <div className="etl-cleaning-report">
        <div className="etl-table-header">
          <span>Cleaning Report</span>
        </div>
        {cleaningReasonEntries.length ? (
          <div className="etl-cleaning-reasons">
            {cleaningReasonEntries.map(([reason, count]) => (
              <div key={reason} className="etl-cleaning-reason">
                <strong>{count}</strong>
                <span>{CLEANING_REASON_LABELS[reason] || reason.replaceAll("_", " ")}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <section className="etl-panel" aria-label="Data Pipeline Launcher">
        <button
          className="etl-launcher-btn"
          type="button"
          onClick={openPanel}
          aria-label="Open data pipeline workspace"
        >
          <span className="etl-launcher-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" className="etl-launcher-icon-svg" focusable="false" aria-hidden="true">
              <path d="M12 4v8" />
              <path d="M8.5 7.5L12 4l3.5 3.5" />
              <rect x="4" y="13" width="16" height="7" rx="2" />
              <path d="M8 16.5h.01M12 16.5h.01M16 16.5h.01" />
            </svg>
          </span>
          <span className="etl-launcher-copy">
            <strong>Import Data</strong>
            <small>{viewLabel}</small>
          </span>
          <span className="etl-launcher-action" aria-hidden="true">
            <svg viewBox="0 0 20 20" className="etl-launcher-action-icon" focusable="false" aria-hidden="true">
              <path d="M7 5l5 5-5 5" />
            </svg>
          </span>
        </button>
      </section>

      {isOpen && (
        <div className="etl-liquid-overlay" onClick={closePanel}>
          <div className="etl-liquid-modal" onClick={(event) => event.stopPropagation()}>
            <div className="etl-liquid-glow etl-liquid-glow-a" />
            <div className="etl-liquid-glow etl-liquid-glow-b" />
            <div className="etl-liquid-header">
              <div className="etl-header-main">
                <p className="card-label">Customer Data Import</p>
                <h2 className="etl-title">Import Your Sales File</h2>
                <p className="etl-subtitle">Select your business, upload a file, review columns, and generate customer insights.</p>
              </div>
              <div className="etl-header-actions">
                <div className="etl-meta-pills">
                  <span className="etl-meta-pill state">◆ {viewLabel}</span>
                  <span className="etl-meta-pill metric">◉ {primaryMetric}</span>
                </div>
                <button className="etl-close-btn" type="button" onClick={closePanel}>
                  ✕ Close
                </button>
              </div>
            </div>

            <div className="etl-liquid-body">

        {view === "upload" && (
          <div className="etl-upload-grid">
            <div className="etl-field-group">
              <label>Business</label>
              <div className="etl-inline-row">
                <div className="etl-business-lock" role="status" aria-live="polite">
                  <span className="etl-business-lock-name">{lockedBusinessLabel}</span>
                  <span className="etl-business-lock-badge">Locked</span>
                </div>
              </div>
              <p className="etl-business-helper">Business is fixed from previous step.</p>
            </div>

            {stagedNewOrgName.trim() ? (
              <p className="etl-domain">
                New org "{stagedNewOrgName}" will be created in database only when upload starts.
              </p>
            ) : null}

            <div className="etl-field-group">
              <label>Sales File</label>
              <div
                className={`etl-dropzone ${isDraggingFile ? "dragging" : ""}`}
                onDragEnter={onDropZoneDragEnter}
                onDragOver={onDropZoneDragOver}
                onDragLeave={onDropZoneDragLeave}
                onDrop={onDropZoneDrop}
              >
                <div className="etl-file-picker-row">
                <input
                  ref={fileInputRef}
                  className="etl-file-input-hidden"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(event) => assignUploadFile(event.target.files?.[0] || null)}
                />
                <button
                  className="etl-file-trigger"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  ⬆ Choose File
                </button>
                <span className="etl-file-chip">{file?.name || "No file selected"}</span>
                </div>
                <p className="etl-dropzone-hint">or drag and drop your file here</p>
              </div>
            </div>

            <div>
              <button
                className="action-btn"
                type="button"
                onClick={onUpload}
                disabled={isBusy || !file || (!selectedOrgId && !stagedNewOrgName.trim())}
              >
                {isBusy ? "Importing..." : "Start Import"}
              </button>
            </div>
          </div>
        )}

        {view === "mapping" && (
          <div>
            <p className="etl-domain">Domain: {mapping.domain_hint || "unknown"}</p>
            <div className="etl-action-row etl-action-row-top">
              <button className="action-btn" type="button" onClick={() => setView("upload")}>Back</button>
              <button className="action-btn" type="button" disabled={isBusy} onClick={onConfirmMapping}>
                {isBusy ? "Saving..." : "Continue"}
              </button>
            </div>
            <div className="etl-table-wrap">
              <table className="etl-table">
                <thead>
                  <tr>
                    <th>File Column</th>
                    <th>Use As</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {mapping.mappings.map((entry, index) => (
                    <tr key={`${entry.raw_column}-${index}`} className={entry.uncertain ? "etl-uncertain" : ""}>
                      <td>{entry.raw_column}</td>
                      <td>
                        <select
                          value={entry.canonical_field || ""}
                          onChange={(event) => updateMapping(index, event.target.value)}
                        >
                          {CANONICAL_FIELDS.map((field) => (
                            <option key={field || "ignore"} value={field}>
                              {field || "ignore"}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{entry.uncertain ? "Needs review" : "Looks good"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === "processing" && (
          <div>
            <ol className="etl-stage-list">
              {PIPELINE_STAGES.map((stage) => {
                const currentIndex = PIPELINE_STAGES.indexOf(status);
                const stageIndex = PIPELINE_STAGES.indexOf(stage);
                let stageClass = "pending";
                if (stageIndex < currentIndex) stageClass = "done";
                if (stageIndex === currentIndex) stageClass = "active";
                return (
                  <li key={stage} className={`etl-stage-item ${stageClass}`}>
                    <span className="etl-stage-dot" />
                    <span>{STAGE_LABELS[stage]}</span>
                  </li>
                );
              })}
            </ol>

            <div className="etl-count-grid">
              <div><strong>{progress.rows_input ?? "-"}</strong><span>Rows Read</span></div>
              <div><strong>{progress.rows_dropped ?? "-"}</strong><span>Rows Skipped</span></div>
              <div><strong>{progress.customers_upserted ?? "-"}</strong><span>Customers Updated</span></div>
            </div>

            {pipelineFailureReason ? <p className="etl-error-box">{pipelineFailureReason}</p> : null}
            {errorLog && errorLog !== pipelineFailureReason ? <p className="etl-error-box">{errorLog}</p> : null}
            {renderCleaningReport()}
          </div>
        )}

        {view === "results" && (
          <div>
            <div className="etl-action-row">
              <button className="action-btn" type="button" onClick={() => window.open(getJobExportUrl(jobId), "_blank")}>Download Results (CSV)</button>
              <button className="action-btn" type="button" onClick={() => setView("upload")}>Import Another File</button>
            </div>

            {renderCleaningReport()}

            <div className="etl-segment-grid">
              {(results?.segments || []).map((segment) => {
                const badgeClass = SEGMENT_CLASS[segment.segment_name] || "badge-lost";
                const recommendation = recommendations.find((item) => item.segment_name === segment.segment_name);
                return (
                  <article key={segment.segment_name} className="etl-segment-card">
                    <span className={`etl-segment-badge ${badgeClass}`}>{segment.segment_name}</span>
                    <h3>{segment.customer_count} customers</h3>
                    <p>Avg score: {segment.avg_total_score}</p>
                    {recommendation?.recommendation_text ? (
                      <details>
                        <summary>Suggested actions</summary>
                        <pre>{recommendation.recommendation_text}</pre>
                      </details>
                    ) : null}
                  </article>
                );
              })}
            </div>

            <div className="etl-table-header">
              <span>Customer List</span>
              <input
                type="text"
                placeholder="Search customer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="etl-table-wrap">
              <table className="etl-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Recency</th>
                    <th>Frequency</th>
                    <th>Monetary</th>
                    <th>Score</th>
                    <th>Segment</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.customer_id}>
                      <td>{record.full_name || "-"}</td>
                      <td>{record.mobile || record.email || "-"}</td>
                      <td>{record.recency_days ?? "-"}</td>
                      <td>{record.frequency ?? "-"}</td>
                      <td>{record.monetary ?? "-"}</td>
                      <td>{record.rfm_total_score ?? "-"}</td>
                      <td>{record.rfm_segment || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
